import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { Breadcrumb } from '@/components/ui/Breadcrumb'
import { useClientDetail } from '@/hooks/useClientDetail'
import { useUpdateClient } from '@/hooks/useUpdateClient'
import { clientSchema, type ClientFormData } from '@/types/client'
import { ArrowLeft } from 'lucide-react'
import toast from 'react-hot-toast'

export default function EditClientPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { client, loading: isLoading, error } = useClientDetail(id)
  const updateClient = useUpdateClient()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    watch,
    reset,
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      type_client: 'particulier',
      prenom: '',
      nom: '',
      email: '',
      phone: '',
      adresse: '',
      company: '',
      siret: '',
      tva_intracommunautaire: '',
      adresse_facturation: '',
    },
  })

  const typeClient = watch('type_client')

  // Populate form when client data loads
  useEffect(() => {
    if (client) {
      const nameParts = (client.full_name || '').split(' ')
      const prenom = nameParts[0] || ''
      const nom = nameParts.slice(1).join(' ') || ''

      reset({
        type_client: (client.type_client as 'particulier' | 'professionnel') || 'particulier',
        prenom,
        nom,
        email: client.email || '',
        phone: client.phone || '',
        adresse: client.address || '',
        company: client.company || '',
        siret: client.siret || '',
        tva_intracommunautaire: client.tva_intracommunautaire || '',
        adresse_facturation: client.adresse_facturation || '',
      })
    }
  }, [client, reset])

  const onSubmit = async (data: ClientFormData) => {
    if (!id) return

    try {
      await updateClient.mutateAsync({
        id,
        full_name: `${data.prenom} ${data.nom}`,
        email: data.email,
        phone: data.phone || null,
        address: data.adresse || null,
        type_client: data.type_client,
        company: data.type_client === 'professionnel' ? (data.company || null) : null,
        siret: data.type_client === 'professionnel' ? (data.siret || null) : null,
        tva_intracommunautaire:
          data.type_client === 'professionnel' ? (data.tva_intracommunautaire || null) : null,
        adresse_facturation: data.adresse_facturation || null,
      })
      toast.success('Client mis a jour avec succes')
      navigate(`/clients/${id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise a jour'
      toast.error(message)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-brown-900 mb-2">
          Client non trouve
        </h2>
        <p className="text-brown-600 mb-4">
          {error || "Le client demande n'existe pas ou a ete supprime."}
        </p>
        <Link to="/clients">
          <Button variant="outline">Retour a la liste</Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <Breadcrumb
        items={[
          { label: 'Clients', href: '/clients' },
          { label: client.full_name || 'Client', href: `/clients/${id}` },
          { label: 'Modifier' },
        ]}
      />

      <Link to={`/clients/${id}`}>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
        >
          Retour
        </Button>
      </Link>

      <Card padding="lg">
        <CardHeader>
          <CardTitle>Modifier le client</CardTitle>
          <p className="text-sm text-brown-600">
            Modifiez les informations du client
          </p>
        </CardHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Client Type */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-brown-700">
              Type de client *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  {...register('type_client')}
                  value="particulier"
                  className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                />
                <span className="text-brown-900">Particulier</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  {...register('type_client')}
                  value="professionnel"
                  className="w-4 h-4 text-mint-600 border-beige-300 focus:ring-mint-500"
                />
                <span className="text-brown-900">Professionnel</span>
              </label>
            </div>
          </div>

          {/* Personal Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Prenom *"
              {...register('prenom')}
              error={errors.prenom?.message}
            />
            <Input
              label="Nom *"
              {...register('nom')}
              error={errors.nom?.message}
            />
          </div>

          <Input
            label="Email *"
            type="email"
            {...register('email')}
            error={errors.email?.message}
          />

          <Input
            label="Telephone"
            type="tel"
            {...register('phone')}
            error={errors.phone?.message}
          />

          <Input
            label="Adresse"
            {...register('adresse')}
            error={errors.adresse?.message}
          />

          {/* Professional Fields */}
          {typeClient === 'professionnel' && (
            <>
              <Input
                label="Nom de l'entreprise *"
                {...register('company')}
                error={errors.company?.message}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="SIRET"
                  {...register('siret')}
                  placeholder="14 chiffres"
                  error={errors.siret?.message}
                />
                <Input
                  label="TVA Intracommunautaire"
                  {...register('tva_intracommunautaire')}
                  error={errors.tva_intracommunautaire?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brown-700 mb-1">
                  Adresse de facturation
                </label>
                <textarea
                  {...register('adresse_facturation')}
                  className="block w-full px-4 py-2 bg-white border border-beige-300 rounded-lg text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent transition-colors duration-200 min-h-[80px]"
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-beige-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/clients/${id}`)}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting || updateClient.isPending}
              disabled={!isDirty}
            >
              Enregistrer les modifications
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
